import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { StyleObject, useStyletron } from 'styletron-react'
import { HERO_PANEL_GRADIENT, HERO_STACK_BREAKPOINT } from './Hero'
import STEP_ARTWORK_STYLES, {
  STEP_ARTWORK,
  STEP_ARTWORK_SCOPE_CLASS,
} from './heroAnimation/stepArtwork'
import type { OverviewContent } from './resolveOverviewState'
import {
  OVERVIEW_SECTION_HEADING_STYLE,
  OVERVIEW_SECTION_STYLE,
  OVERVIEW_SECTION_SUB_STYLE,
} from './sectionLayout'

const t = buildTranslate('order_feedback')

// Number bubble size. No design token for a plain numeral badge, so it's
// named here rather than inventing a palette entry for a single-use size.
const STEP_NUMBER_SIZE = '28px'

// Id of the single injected <style> element carrying the copied artwork CSS.
const STEP_ARTWORK_STYLE_ID = 'of-step-art-styles'

// Where the intro's closing word points. The page selects its tab from the
// location hash (see this scene's index.tsx and its `readTabFromHash`), so a
// router link that sets the hash is all a tab switch takes — and it stays a
// link, which a raw <a href="#settings"> could not: a fragment navigation
// notifies no history listener, so the tab would never change.
const SETTINGS_TAB_HASH = '#settings'

// The intro's inline link. Deliberately not OVERVIEW_TEXT_LINK_STYLE: that
// one dresses a demoted CTA (bold, 14px, inline-flex) and would set this word
// apart from the sentence carrying it. Only the colour and the hover
// underline are its own; everything else is inherited from the paragraph.
const INTRO_LINK_STYLE: StyleObject = {
  color: palette.belizeHole,
  textDecoration: 'none',
  ':hover': { textDecoration: 'underline' },
}

// Reuses the hero's stack/unstack boundary rather than picking a second one,
// so the whole Overview reflows at a single width.
const STEP_GRID_MEDIA_QUERY = `@media (min-width: ${HERO_STACK_BREAKPOINT}px)`

// Exported for the same reason as the hero's grid style: styletron's css() is
// a no-op in jsdom, so the media-query branch can only be asserted on the
// style object, never on rendered output.
//
// context:
//   The design is three across and carries no media query of its own — below
//   about 1180px it simply overflows its viewport. Three 380px cards cannot
//   shrink into a phone without the 208px artwork panel turning into a
//   portrait sliver and every step's copy setting one word per line, so the
//   grid drops to a single column below the breakpoint. One column rather
//   than two: two would need a second breakpoint the rest of the app has no
//   precedent for. Needs design sign-off — there is no mobile reference.
export const STEP_GRID_STYLE: StyleObject = {
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '18px',
  [STEP_GRID_MEDIA_QUERY]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
}

// Injects the step artwork's stylesheet once per document, mirroring how
// HeroIllustration injects its own copied stylesheet.
const useStepArtworkStyles = (): void => {
  useEffect(() => {
    if (document.getElementById(STEP_ARTWORK_STYLE_ID)) return
    const el = document.createElement('style')
    el.id = STEP_ARTWORK_STYLE_ID
    el.textContent = STEP_ARTWORK_STYLES
    document.head.appendChild(el)
  }, [])
}

type Props = {
  content: OverviewContent
  isSettingsLinkEnabled?: boolean
}

// The three-step "How it works" explainer beneath the promo block: a
// heading and intro sit outside any card, then three separate cards follow,
// each with a tinted image stage on top. Visible only where the resolver's
// `howItWorks` descriptor says so (the `new` and `thanx` states) — every key
// it renders comes from that descriptor, never built here.
//
// context:
//   Each card's illustration is copied from the prototype rather than being
//   an image asset — they are small HTML/CSS mockups, which is why an earlier
//   port that looked for image files left three empty tinted panels. The
//   intro's closing word, "settings", is a link onto this page's own Settings
//   tab. That tab is hidden only in `thirdparty`, `rai_all` and
//   `rai_thirdparty`, and this whole section renders in `new` and `thanx`
//   alone. It is greyed out while live updates are disabled, so the word is
//   plain text then (`isSettingsLinkEnabled`).
const HowItWorks = ({ content, isSettingsLinkEnabled = true }: Props) => {
  const [css] = useStyletron()
  const { howItWorks } = content
  // Before the early return: hooks cannot run conditionally.
  useStepArtworkStyles()

  if (!howItWorks.visible) return null

  return (
    <section className={css(OVERVIEW_SECTION_STYLE)}>
      {howItWorks.headingKey && (
        <h3 className={css(OVERVIEW_SECTION_HEADING_STYLE)}>
          {t(howItWorks.headingKey)}
        </h3>
      )}

      {howItWorks.introKey && (
        <p className={css(OVERVIEW_SECTION_SUB_STYLE)}>
          {t(howItWorks.introKey)}
          {/* The space belongs to the link rather than to the sentence, so a
              descriptor that ever omits the link closes the sentence cleanly
              instead of leaving a gap before its full stop. */}
          {howItWorks.introLinkKey && (
            <>
              {' '}
              {isSettingsLinkEnabled ? (
                <Link to={SETTINGS_TAB_HASH} className={css(INTRO_LINK_STYLE)}>
                  {t(howItWorks.introLinkKey)}
                </Link>
              ) : (
                t(howItWorks.introLinkKey)
              )}
            </>
          )}
          {howItWorks.introSuffixKey && t(howItWorks.introSuffixKey)}
        </p>
      )}

      <div className={css(STEP_GRID_STYLE)}>
        {howItWorks.steps.map((step, index) => (
          <div
            key={step.titleKey}
            data-testid="how-it-works-step"
            className={css({
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: palette.white,
              border: `1px solid ${palette.grey20}`,
              borderRadius: '8px',
              overflow: 'hidden',
            })}
          >
            {/* The tinted stage, carrying the step's mockup copied from the
                prototype. Centring, clipping and the bottom rule are the
                prototype's own `.step-img` values. */}
            <div
              data-testid="how-it-works-step-image"
              className={`${STEP_ARTWORK_SCOPE_CLASS} ${css({
                height: '208px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                borderBottom: `1px solid ${palette.grey20}`,
                backgroundImage: HERO_PANEL_GRADIENT,
              })}`}
              aria-hidden="true"
              // Build-time constant copied from the design — no untrusted
              // input reaches this.
              dangerouslySetInnerHTML={{ __html: STEP_ARTWORK[index] ?? '' }}
            />
            <div className={css({ padding: '20px 22px' })}>
              <div
                className={css({
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: STEP_NUMBER_SIZE,
                  height: STEP_NUMBER_SIZE,
                  borderRadius: '50%',
                  backgroundColor: palette.belizeHole10,
                  color: palette.belizeHole,
                  fontSize: '13px',
                  fontWeight: 700,
                  marginBottom: '12px',
                })}
                aria-hidden="true"
              >
                {index + 1}
              </div>
              {/* The design sets neither line-height here — both inherit its
                  global 1.5, giving 24px on the 16px title and 21px on the
                  14px body. This app has no such global, so the ratio is
                  written out: without it the title collapses to the font's
                  own `normal` and the body inherits a 16px step that belongs
                  to section copy, not to a card. */}
              <h4
                className={css({
                  fontSize: '16px',
                  lineHeight: '24px',
                  fontWeight: 700,
                  color: palette.grey90,
                  margin: '0 0 6px',
                })}
              >
                {t(step.titleKey)}
              </h4>
              <p
                className={css({
                  fontSize: '14px',
                  lineHeight: '21px',
                  color: palette.grey70,
                  margin: 0,
                })}
              >
                {t(step.bodyKey)}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

export default HowItWorks
